import { request } from "../client";

import type {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  UpdateSubscriptionRequest,
  UpdateSubscriptionResponse,
} from "../types/subscription";
import type {
  PublicSubscriptionPlan,
  AccountSubscriptionResponse,
} from "@lingoshade/domain";

export interface SubscriptionLimitsData {
  textCreation: {
    current: number;
    limit: number;
    unlimited: boolean;
    resetsAt: string | null;
    quotaPeriod: "LIFETIME" | "MONTHLY";
  };
  dictionary: {
    current: number;
    limit: number;
    unlimited: boolean;
  };
  voices: {
    hasAllVoices: boolean;
  };
  dictation: {
    current?: number;
    quotaPeriod?: "LIFETIME" | "UNLIMITED";
    dailyLimit: number;
    unlimited: boolean;
  };
  composition: {
    dailyLimit: number;
    unlimited: boolean;
  };
}

export interface SubscriptionLimitsResponse {
  success: boolean;
  data: SubscriptionLimitsData;
}

export function createSubscriptionsEndpoints(baseUrl: string): {
  getPlans: (token?: string | null) => Promise<PublicSubscriptionPlan[]>;
  getMySubscription: (
    token?: string | null,
  ) => Promise<AccountSubscriptionResponse>;
  getSubscriptionLimits: (
    token?: string | null,
  ) => Promise<SubscriptionLimitsResponse>;
  create: (
    payload: CreateSubscriptionRequest,
    token?: string | null,
  ) => Promise<CreateSubscriptionResponse>;
  cancel: (
    id: string,
    payload: CancelSubscriptionRequest | undefined,
    token?: string | null,
  ) => Promise<CancelSubscriptionResponse>;
  update: (
    payload: UpdateSubscriptionRequest,
    token?: string | null,
  ) => Promise<UpdateSubscriptionResponse>;
} {
  return {
    getPlans(token?: string | null): Promise<PublicSubscriptionPlan[]> {
      return request<PublicSubscriptionPlan[]>(
        baseUrl,
        "/subscriptions/plans",
        {
          method: "GET",
          token,
        },
      );
    },
    getMySubscription(
      token?: string | null,
    ): Promise<AccountSubscriptionResponse> {
      return request<AccountSubscriptionResponse>(
        baseUrl,
        "/subscriptions/my-subscription",
        {
          method: "GET",
          token,
        },
      );
    },
    getSubscriptionLimits(
      token?: string | null,
    ): Promise<SubscriptionLimitsResponse> {
      return request<SubscriptionLimitsResponse>(
        baseUrl,
        "/subscription/limits",
        {
          method: "GET",
          token,
        },
      );
    },
    create(
      payload: CreateSubscriptionRequest,
      token?: string | null,
    ): Promise<CreateSubscriptionResponse> {
      return request<CreateSubscriptionResponse>(
        baseUrl,
        "/subscriptions/subscribe",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },
    cancel(
      id: string,
      payload: CancelSubscriptionRequest | undefined,
      token?: string | null,
    ): Promise<CancelSubscriptionResponse> {
      return request<CancelSubscriptionResponse>(
        baseUrl,
        `/subscriptions/${id}/cancel`,
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },
    update(
      payload: UpdateSubscriptionRequest,
      token?: string | null,
    ): Promise<UpdateSubscriptionResponse> {
      return request<UpdateSubscriptionResponse>(
        baseUrl,
        "/subscriptions/update",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },
  };
}
